import { shuffle } from "../../../services/shuffle";

/** CSS `aspect-ratio` value (e.g. `"1 / 1"`, `"960 / 1419"`). */
export type PageAspectRatio = string;

export type PageCreateOptions = {
  showPageNumber?: boolean;
  /** When set, the reader frames the image in this aspect box (`object-fit: contain`). */
  aspectRatio?: PageAspectRatio;
};

/** Square game / decodable spreads. */
const PAGE_AR_SQUARE: PageAspectRatio = "1 / 1";
/** Portrait trim (aligned with story picker cover frame). */
const PAGE_AR_TRIM: PageAspectRatio = "960 / 1419";

export type ImageMatchGamePages = {
  correctPage: Page;
  wrongPages: Page[];
  allPages: Page[];
};

export class Story {
  /**
   * @param readingPages — Full read order: `cover.webp`, `intro-*`, `page-*`, `back.webp` (or legacy: only `page-*`).
   * @param gamePages — Decodable `page-*` only; post-read image match samples from this list.
   */
  constructor(
    public readonly name: string,
    public readonly readingPages: Page[],
    public readonly gamePages: Page[],
  ) {}

  /** Picker thumbnail: first screen of the read (usually `cover.webp`). */
  get coverImage(): string {
    return this.readingPages[0]?.image ?? "";
  }

  getPreviousPage(currentPage?: Page): Page | undefined {
    if (!currentPage) return;

    const index = this.readingPages.findIndex((p) => p === currentPage);
    const previousPage = this.readingPages[index - 1];
    return previousPage ? previousPage : undefined;
  }

  getNextPage(currentPage?: Page): Page | undefined {
    if (!currentPage) return;

    const index = this.readingPages.findIndex((p) => p === currentPage);
    const nextPage = this.readingPages[index + 1];
    return nextPage ? nextPage : undefined;
  }

  hasPreviousPage(currentPage?: Page): boolean {
    return !!this.getPreviousPage(currentPage);
  }

  hasNextPage(currentPage?: Page): boolean {
    return !!this.getNextPage(currentPage);
  }

  getPagesForImageMatchGame(): ImageMatchGamePages {
    const pool = this.gamePages;
    if (pool.length === 0) {
      throw new Error(`Story "${this.name}" has no game pages`);
    }
    const correctPage = this.getRandomPage(pool);
    const candidates = pool.filter((p) => p !== correctPage);
    const wrongCount = Math.min(2, candidates.length);
    const wrongPages: Page[] = [];
    const remaining = [...candidates];
    for (let i = 0; i < wrongCount; i++) {
      const idx = Math.floor(Math.random() * remaining.length);
      wrongPages.push(remaining.splice(idx, 1)[0]);
    }

    return {
      correctPage,
      wrongPages,
      allPages: shuffle([correctPage, ...wrongPages]),
    };
  }

  getRandomPage(pages: Page[]): Page {
    return pages[Math.floor(Math.random() * pages.length)];
  }

  /**
   * Read: `cover` → `intro-*` → `page-*` → `back`. Image match: `page-*` only.
   */
  static async TheTrap(): Promise<Story> {
    const cover = await Page.create(0, "", "the-trap/cover.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro1 = await Page.create(0, "", "the-trap/intro-1.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro2 = await Page.create(0, "", "the-trap/intro-2.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro3 = await Page.create(0, "", "the-trap/intro-3.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const gamePages = [
      await Page.create(
        1,
        "Mags and Jax have flat brass slabs. The brass slabs were in Mags's bag.",
        "the-trap/page-1.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        2,
        "Las is a bad crab. In a snap, he grabs the slab from the bag.",
        "the-trap/page-2.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        3,
        "Las snags it fast and drags it to his pad. It is in a trap.",
        "the-trap/page-3.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        4,
        "Drat! Mags is mad and yaps at Jax. They have a spat.",
        "the-trap/page-4.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        5,
        "Las is a brat! Mags and Jax want the slab.",
        "the-trap/page-5.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        6,
        "They flag a pal to get to Las's pad. They want to find the slab.",
        "the-trap/page-6.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        7,
        "Las brags to Mags and Jax, but he does not blab the trap.",
        "the-trap/page-7.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        8,
        "They look on the mat, but do not see the slab. They look in his cap, but no slab.",
        "the-trap/page-8.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        9,
        "Tap! Tap! They jab at the rag on the vat. The trap! The rag was Las's trap for the slab.",
        "the-trap/page-9.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        10,
        "Mags and Jax are glad to have the flat brass slabs! They clap the slabs!",
        "the-trap/page-10.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        11,
        "They bat at it with the slabs! They have crab! ",
        "the-trap/page-11.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
    ];
    const back = await Page.create(0, "", "the-trap/back.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const readingPages = [cover, intro1, intro2, intro3, ...gamePages, back];
    return new Story("The Trap!", readingPages, gamePages);
  }

  /**
   * Read: `cover` → `intro-*` → `page-*` → `back`. Image match: `page-*` only.
   */
  static async TheFest(): Promise<Story> {
    const cover = await Page.create(0, "", "the-fest/cover.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro1 = await Page.create(0, "", "the-fest/intro-1.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro2 = await Page.create(0, "", "the-fest/intro-2.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const gamePages = [
      await Page.create(
        1,
        `Meg and Rex beg to go to the fest. Meg and Rex tell Fred, "We bet our mom will say yes!."`,
        "the-fest/page-1.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        2,
        `"Please, mom!!" Meg and Rex pled. Mom says, "Don't be a pest, but yes you can go to the fest."`,
        "the-fest/page-2.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        3,
        `"But, I am on a quest to edit the fest." "I want it to be the best." "Yes!" I want it to be the very best!" Fred yells with zest.`,
        "the-fest/page-3.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        4,
        `"My mom let me go too," said Fred. It is not time to go yet. They will go at ten.`,
        "the-fest/page-4.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        5,
        `They set up on their bed and slept. They want to rest before the fest. The red peg (the bed's leg) bent! The bed fell. It sent Meg, Rex, and Fred off the bed. Fred led them as they crept to get help.`,
        "the-fest/page-5.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        6,
        `We left to get back to Meg and Rex's mom. "Welp," said Fred. "The fest was not the best."`,
        "the-fest/page-6.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        7,
        `We went west to see the rest of the fest. We met Lex at his nest. He lent us his jet. He fed us and let us rest. Lex led us to the exit.`,
        "the-fest/page-7.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        8,
        `"Help!" Fred, Meg, and Rex yelp! They all have wept. Meg and Rex's mom has to come help.`,
        "the-fest/page-8.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        9,
        `It is ten! It is time to go to the fest! They all left and went off to the fest! They sped to the fest fast!`,
        "the-fest/page-9.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        10,
        `The fest has a tent you can rent. They slept in the tent! Oh no! It was wet and had webs! We use the net to fix the webs.`,
        "the-fest/page-10.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
    ];
    const back = await Page.create(0, "", "the-fest/back.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const readingPages = [cover, intro1, intro2, ...gamePages, back];
    return new Story("The Fest", readingPages, gamePages);
  }

  /**
   * Read: `cover` → `intro-*` → `page-*` → `back`. Image match: `page-*` only.
   */
  static async TheMix(): Promise<Story> {
    const cover = await Page.create(0, "", "the-mix/cover.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro1 = await Page.create(0, "", "the-mix/intro-1.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro2 = await Page.create(0, "", "the-mix/intro-2.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro3 = await Page.create(0, "", "the-mix/intro-3.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const gamePages = [
      await Page.create(
        1,
        "Sid the pig, Zig and Jig the twin kits, and Kim the kid are in a grid. In the grid, Sid is in a pit, Zig and Jig skip in the crib, and Kim sits in the big rig.",
        "the-mix/page-1.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        2,
        "Kim snips the skin of a twig. Sid digs and swims in the grit in his pit.",
        "the-mix/page-2.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        3,
        "Sid, Zig, Jig, and Kim want to fix a mix of ribs and figs! Kim and Sid clip the big ribs.",
        "the-mix/page-3.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        4,
        "Kim, Sid, Zig and Jig fix the mix and grin. It's a win!",
        "the-mix/page-4.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        5,
        "Zig and Jig skim for figs. Watch out! There is grit! Zig and Jig trip and slip on the grit. They skid to the figs. It is OK!",
        "the-mix/page-5.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        6,
        "They get six limp figs and trim them. Kim and Sid mix the big ribs and the six figs.",
        "the-mix/page-6.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        7,
        "Kim, Zig, Jig, and Sid, quit the gig. Zip! The mix is in the bin. It is not a win!",
        "the-mix/page-7.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        8,
        "It drips and drips! Kim has a fit. She has a tip, quit! Do not mix figs and ribs. It is her tidbit.",
        "the-mix/page-8.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        9,
        "Kim, Sid, Zig, and Jig fix the mix in the tin. The mix is to the brim of the tin. It has a hint of mint in it.",
        "the-mix/page-9.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        10,
        "The twin kits had a swig. It is grim, they spit it.",
        "the-mix/page-10.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        11,
        "Kim bit her slim lip. She grabs the tin and flips it.",
        "the-mix/page-11.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
    ];
    const back = await Page.create(0, "", "the-mix/back.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const readingPages = [cover, intro1, intro2, intro3, ...gamePages, back];
    return new Story("The Mix", readingPages, gamePages);
  }

  /**
   * Read: `cover` → `intro-*` → `page-*` → `back`. Image match: `page-*` only.
   */
  static async TheBlob(): Promise<Story> {
    const cover = await Page.create(0, "", "the-blob/cover.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro1 = await Page.create(0, "", "the-blob/intro-1.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro2 = await Page.create(0, "", "the-blob/intro-2.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro3 = await Page.create(0, "", "the-blob/intro-3.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro4 = await Page.create(0, "", "the-blob/intro-4.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const gamePages = [
      await Page.create(
        1,
        "Mom sobs at Rob, Hob, Dom, and Mox. Rob is a blob. Hob is a slob. Dom is a snob. Mox is a slop.",
        "the-blob/page-1.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        2,
        "Rob the blob does not like his job. He does not do the blog. He does not jot or log a plot at all.",
        "the-blob/page-2.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        3,
        "Hob is a slob. There is not a spot to plop. It is as bad as the fog of rot in the hog's bog.",
        "the-blob/page-3.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        4,
        "Dom is a snot of a snob. She clops around like a fox in a fog.",
        "the-blob/page-4.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        5,
        "Rob, Hob, Dom, and Mox all sob to Mom. She nods and says it is OK. Mom does not have a blob, a slob, a snob, or a slop. She plops hot cod from the pot to eat.",
        "the-blob/page-5.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        6,
        "Mox is a big slop. He smells like rot. He does not want to blot off the slop.",
        "the-blob/page-6.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        7,
        "Mom wants Rob, Hob, Dom, and Mox to not be a flop. Mom says it has to stop.",
        "the-blob/page-7.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        8,
        "Mox the slop needs to blot off the slop. He flops in the tub to sop off all the slop.",
        "the-blob/page-8.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        9,
        "Dom is not a snob. She does not clop like a snot in a fog. She plods and prods along and nods at friends.",
        "the-blob/page-9.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        10,
        "She plops Rob, Hob, Dom, and Mox on the cot. They drop. It has to stop Mom says to hop to it!",
        "the-blob/page-10.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        11,
        "Rob has to do his job. He jots a good blog on cobs, crops, and cost.",
        "the-blob/page-11.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
      await Page.create(
        12,
        "Hob can not be a slob. He plods along and props all of his lost, soft frogs on top of the slots on the logs. Hop Hop! He blots and sops up the spots and gobs of rot.",
        "the-blob/page-12.webp",
        {
          aspectRatio: PAGE_AR_SQUARE,
        },
      ),
    ];
    const back = await Page.create(0, "", "the-blob/back.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const readingPages = [
      cover,
      intro1,
      intro2,
      intro3,
      intro4,
      ...gamePages,
      back,
    ];
    return new Story("The Blob", readingPages, gamePages);
  }

  /**
   * Read: `cover` → `intro-*` → `page-*` → `back`. Image match: `page-*` only.
   */
  static async Hux(): Promise<Story> {
    const cover = await Page.create(0, "", "hux/cover.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro1 = await Page.create(0, "", "hux/intro-1.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro2 = await Page.create(0, "", "hux/intro-2.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const intro3 = await Page.create(0, "", "hux/intro-3.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const gamePages = [
      await Page.create(
        1,
        "Hux the pug is a nut! He likes to rub around in the mud. It all makes a rut in the run.",
        "hux/page-1.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        2,
        "Hux jumps up and is on the rug. He is not in the mud. Whew! He did not want the suds in the tub! His mum gives Hux a big hug!",
        "hux/page-2.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        3,
        "His mum sees the sum of the mud. She is glum. She just had Hux in the tub. She does not want to put him in the tub. Are we in a rut? She tugs at Hux to the tub.",
        "hux/page-3.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        4,
        "But his mum says it is not fun. She wants him in the tub, but Hux snubs his mum.",
        "hux/page-4.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        5,
        "The tub is a must! Yup, the tub is a big must! His mum has him sit on his bum to lug him to the tub. She will not give him a hug.",
        "hux/page-5.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        6,
        "Hux gets on the bus to see his bud, Jud. Jud and Hux see more mud and they dug and dug.",
        "hux/page-6.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        7,
        "He jumps off the bus at the hub to hunt for grub and a gulp. He got a sub on a bun and a plum. He cut up the sub. Yum, yum says his gut!",
        "hux/page-7.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        8,
        "His mum dumps him in the tub and puts in the plug. Rub-a-dub-dub, Hux is in the tub! He likes to be a sub in the tub.",
        "hux/page-8.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        9,
        "His mum puts suds all over the pug! She uses a jug to get all of the mud and suds off of the pug. Hux is out of the tub and his mum sops up all of the water.",
        "hux/page-9.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
      await Page.create(
        10,
        "Hux is now snug on the rug. He nods off. He gets on a bus to hunt for some grub. He acts like such a smug stud. He hums to the drum on the bus.",
        "hux/page-10.webp",
        { aspectRatio: PAGE_AR_SQUARE },
      ),
    ];
    const back = await Page.create(0, "", "hux/back.webp", {
      showPageNumber: false,
      aspectRatio: PAGE_AR_TRIM,
    });
    const readingPages = [cover, intro1, intro2, intro3, ...gamePages, back];
    return new Story("Hux", readingPages, gamePages);
  }
}

export type StoryChoiceId =
  | "the-trap"
  | "the-fest"
  | "the-mix"
  | "the-blob"
  | "hux";

/** Story picker + `loadStoryById`; uncomment a row to ship that title. */
export const STORY_LOADERS: {
  id: StoryChoiceId;
  title: string;
  load: () => Promise<Story>;
}[] = [
  { id: "the-trap", title: "The Trap!", load: Story.TheTrap },
  { id: "the-fest", title: "The Fest", load: Story.TheFest },
  { id: "the-mix", title: "The Mix", load: Story.TheMix },
  { id: "the-blob", title: "The Blob", load: Story.TheBlob },
  { id: "hux", title: "Hux", load: Story.Hux },
];

export function loadStoryById(id: string): Promise<Story> {
  const choice = STORY_LOADERS.find((c) => c.id === id);
  return (choice?.load ?? Story.TheTrap)();
}

/**
 * Picker thumbnail: same URL as {@link Story.coverImage} — the first `readingPages`
 * entry (dedicated `cover.webp` when present, otherwise the first spread).
 */
export async function loadStoryCoverImageUrl(
  id: StoryChoiceId,
): Promise<string> {
  const story = await loadStoryById(id);
  return story.coverImage;
}

export class Page {
  constructor(
    public readonly number: number,
    public readonly text: string,
    public readonly image: string,
    /** When false, story UI omits body text and the page number chip (cover / intro / back). */
    public readonly showPageNumber: boolean = true,
    /** CSS `aspect-ratio` for framed layout; omit for natural image height. */
    public readonly aspectRatio?: PageAspectRatio,
  ) {}

  static async create(
    number: number,
    text: string,
    imagePath: string,
    options?: PageCreateOptions,
  ) {
    const showPageNumber = options?.showPageNumber ?? true;
    const aspectRatio = options?.aspectRatio;
    const imageModule = await import(`../stories/${imagePath}`);
    return new Page(
      number,
      text,
      imageModule.default,
      showPageNumber,
      aspectRatio,
    );
  }
}
